import {
  useCallback, useState, memo, useMemo,
} from 'react';
import {
  Divider,
  Grid,
  PanelContent,
  SearchableTextField,
  useTranslations,
  usePubSub,
  Button,
  PlusIcon,
  useSnackbar,
  Tooltip,
  ActionButton,
  MoveIcon,
  usePopper,
  FilterButton,
  ShareIcon,
  encryptSymmetric,
  ErrorIcon,
  useMapKeyValueExtractor,
  PromptModal,
  Typography,
  ISearchableTextFieldProps,
  IFilterButtonProps,
} from '@uniqkey-frontend/shared-app';
import { EmployeeAccountStatus, Ownership } from '@uniqkey-backend-organization-web/api-client';
import PubSubEventEnum from '../../../../enums/PubSubEventEnum';
import useEmployeeGroupSecureNotesTable, {
  IEmployeeGroupSecureNotesTableRow,
} from '../../../../hooks/tables/useEmployeeGroupSecureNotesTable';
import EmployeeSecureNotesTable from '../../../../components/tables/EmployeeSecureNotesTable';
import CreateSecureNoteModal, {
  ICreateSecureNoteReturnValue,
} from '../../../../components/CreateSecureNoteModal';
import useVaultsToEmployeeAccountsAPI from '../../../../hooks/useVaultsToEmployeeAccountsAPI';
import {
  useDeleteVaultsToEmployeeAccounts,
  useGetEmployeeAccountById,
} from '../../../../hooks/reactQuery';
import EmployeeSecureNotesTabFilter, {
  IEmployeeSecureNotesTabFilterSubmitResult,
} from './components/EmployeeSecureNotesTabFilter';
import ShareVaultsToGroupModal from '../../../../components/ShareVaultsToGroupModal';
import { useCompanionApplicationContext } from '../../../../contexts/CompanionApplicationContext';
import useVaultsAPI from '../../../../hooks/useVaultsAPI';
import VaultTypeEnum from '../../../../enums/VaultTypeEnum';
import { logException } from '../../../../services/sentryService';
import { getActiveOrganizationId } from '../../../../services/organizationService';
import { generateTooltipTitle } from '../../../../helpers/tooltips';
import { useTrustedPortalStore } from '../../../../modules/TrustedPortalModule/store';

interface IEmployeeSecureNotesTabProps {
  employeeAccountId: string;
  employeeAccountStatus: EmployeeAccountStatus;
}

const EmployeeSecureNotesTab = (props: IEmployeeSecureNotesTabProps) => {
  const { employeeAccountId, employeeAccountStatus } = props;
  const { t } = useTranslations();
  const [isCreateSecureNoteModalOpen, setIsCreateSecureNoteModalOpen] = useState<boolean>(false);
  const [isCreateSecureNoteLoading, setIsCreateSecureNoteLoading] = useState<boolean>(false);
  const [isDeleteSecureNoteModalOpen, setIsDeleteSecureNoteModalOpen] = useState<boolean>(false);
  const [isDeleteSecureNoteLoading, setIsDeleteSecureNoteLoading] = useState<boolean>(false);
  const [isMoveSecureNotesModalOpen, setIsMoveSecureNotesModalOpen] = useState<boolean>(false);
  const [isMoveSecureNotesLoading, setIsMoveSecureNotesLoading] = useState<boolean>(false);
  const [isShareSecureNotesModalOpen, setIsShareSecureNotesModalOpen] = useState<boolean>(false);

  const handleCreateSecureNoteModalOpen = useCallback(
    () => setIsCreateSecureNoteModalOpen(true),
    [],
  );
  const handleCreateSecureNoteModalClose = useCallback(
    () => setIsCreateSecureNoteModalOpen(false),
    [],
  );
  const handleDeleteSecureNoteModalOpen = useCallback(
    () => setIsDeleteSecureNoteModalOpen(true),
    [],
  );
  const handleDeleteSecureNoteModalClose = useCallback(
    () => setIsDeleteSecureNoteModalOpen(false),
    [],
  );
  const handleMoveSecureNotesModalOpen = useCallback(() => setIsMoveSecureNotesModalOpen(true), []);
  const handleMoveSecureNotesModalClose = useCallback(
    () => setIsMoveSecureNotesModalOpen(false),
    [],
  );
  const handleShareSecureNotesModalOpen = useCallback(
    () => setIsShareSecureNotesModalOpen(true),
    [],
  );
  const handleShareSecureNotesModalClose = useCallback(
    () => setIsShareSecureNotesModalOpen(false),
    [],
  );

  const { showError, showWarning, showSuccess } = useSnackbar();
  const { createVaultsSecureNoteToEmployeeAccounts } = useVaultsToEmployeeAccountsAPI();
  const { moveVaultsToPrivateKeychain } = useVaultsAPI();
  const { mutate: mutateDeleteSecureNote } = useDeleteVaultsToEmployeeAccounts();
  const { symmetricKey } = useCompanionApplicationContext();
  const {
    isOpen: isFilterOpen,
    anchorEl: filterAnchorEl,
    setPopperIsOpen: setIsFilterOpen,
  } = usePopper();
  const toggleIsFilterOpen = useCallback<NonNullable<IFilterButtonProps['onChange']>>(
    (event) => setIsFilterOpen(!isFilterOpen, event),
    [setIsFilterOpen, isFilterOpen],
  );
  const handleFilterClose = useCallback(() => setIsFilterOpen(false), [setIsFilterOpen]);

  const {
    selectedSecureNotes,
    refetch,
    searchQuery,
    setSearchQuery,
    filterValues,
    setFilterValues,
    isFilterActive,
    numberOfActiveFilters,
    resetActivePage,
    resetSelectedRows,
    ...restTableProps
  } = useEmployeeGroupSecureNotesTable({
    persistentFilters: { employeeAccountId },
    noDataMessageKey: 'secureNotesTab.table.noData',
  });

  const { data: employeeAccount } = useGetEmployeeAccountById(
    { employeeAccountId },
  );

  const {
    values: selectedSecureNotesAsObjects, keys: selectedSecureNotesIds,
  } = useMapKeyValueExtractor<IEmployeeGroupSecureNotesTableRow>(selectedSecureNotes);

  const activeOrganizationId = getActiveOrganizationId();
  const isTrustedPortalEnabled = useTrustedPortalStore.useIsEnabledByOrganizationId()[
    activeOrganizationId!
  ] ?? false;

  const {
    isMoveDisabled,
    isShareDisabled,
    isRemoveDisabled,
  } = useMemo(() => {
    if (!selectedSecureNotesAsObjects.length) {
      return { isMoveDisabled: true, isShareDisabled: true, isRemoveDisabled: true };
    }
    let disableMove = false;
    let disableShare = false;
    let disableRemove = false;
    selectedSecureNotesAsObjects.forEach(({ ownership }) => {
      if (ownership === Ownership.Groups) {
        disableMove = true;
        disableRemove = true;
      }
      if (ownership === Ownership.Employees) {
        disableShare = true;
      }
    });
    return {
      isMoveDisabled: disableMove,
      isShareDisabled: disableShare,
      isRemoveDisabled: disableRemove,
    };
  }, [selectedSecureNotesAsObjects]);

  const handleCreateSecureNote = useCallback(async (params: ICreateSecureNoteReturnValue) => {
    const { name, note } = params;
    setIsCreateSecureNoteLoading(true);
    try {
      await createVaultsSecureNoteToEmployeeAccounts({
        employeeAccountId,
        name,
        note: encryptSymmetric({
          key: symmetricKey,
          string: note,
          fallbackValue: null,
        }),
      });
      if (isTrustedPortalEnabled) {
        showSuccess({
          text: t('trustedPortalSuccessNotifications.secureNote.created'),
        });
      } else {
        showWarning({
          text: t('createSecureNoteModal.approvalOnMobileToast'),
        });
      }
      handleCreateSecureNoteModalClose();
    } catch (e) {
      showError({
        text: t('common.somethingWentWrong'),
      });
      logException(e, {
        message: 'EmployeeSecureNotesTab/handleCreateSecureNote exception',
      });
    } finally {
      setIsCreateSecureNoteLoading(false);
    }
  }, [
    createVaultsSecureNoteToEmployeeAccounts,
    handleCreateSecureNoteModalClose,
    employeeAccountId,
    symmetricKey,
    isTrustedPortalEnabled,
    showSuccess,
    showWarning,
    showError,
    t,
  ]);

  const handleDeleteSecureNote = useCallback(async () => {
    setIsDeleteSecureNoteLoading(true);
    mutateDeleteSecureNote({
      vaultIds: selectedSecureNotesIds, employeeAccountId,
    }, {
      onSuccess: ({ failCount, successCount }) => {
        if (successCount) {
          showSuccess({
            text: t('deleteSecureNoteFromEmployeeModal.successMessage', { count: successCount }),
          });
        }
        if (failCount) {
          showError({
            text: t('deleteSecureNoteFromEmployeeModal.errorMessage', { count: failCount }),
          });
        }
        handleDeleteSecureNoteModalClose();
        resetSelectedRows();
        resetActivePage();
      },
      onError: () => showError({ text: t('common.somethingWentWrong') }),
      onSettled: () => setIsDeleteSecureNoteLoading(false),
    });
  }, [
    employeeAccountId,
    selectedSecureNotesIds,
    mutateDeleteSecureNote,
    handleDeleteSecureNoteModalClose,
    showError,
    showSuccess,
    resetSelectedRows,
    resetActivePage,
    t,
  ]);

  const handleMoveSecureNotes = useCallback(async () => {
    try {
      setIsMoveSecureNotesLoading(true);
      await moveVaultsToPrivateKeychain({ vaultIds: selectedSecureNotesIds, employeeAccountId });
      if (isTrustedPortalEnabled) {
        showSuccess({
          text: t('trustedPortalSuccessNotifications.secureNote.moved'),
        });
      } else {
        showWarning({
          text: t('moveSecureNotesModal.approvalOnMobileToast'),
        });
      }
      resetSelectedRows();
      handleMoveSecureNotesModalClose();
    } catch (e) {
      showError({
        text: t('common.somethingWentWrong'),
      });
      logException(e, {
        message: 'EmployeeSecureNotesTab/handleMoveSecureNotes exception',
      });
    } finally {
      setIsMoveSecureNotesLoading(false);
    }
  }, [
    moveVaultsToPrivateKeychain,
    selectedSecureNotesIds,
    isTrustedPortalEnabled,
    showWarning,
    showError,
    showSuccess,
    handleMoveSecureNotesModalClose,
    employeeAccountId,
    resetSelectedRows,
    t,
  ]);

  const handleRefetch = useCallback(() => refetch(true), [refetch]);
  usePubSub(PubSubEventEnum.DATA_SYNCHRONIZATION_VAULT, handleRefetch);

  const handleSearchChange = useCallback<ISearchableTextFieldProps['onChange']>(
    (debouncedValue) => {
      setSearchQuery(debouncedValue);
      resetActivePage();
    },
    [setSearchQuery, resetActivePage],
  );

  const handleFilterSubmit = useCallback((
    updatedValues: IEmployeeSecureNotesTabFilterSubmitResult,
  ) => {
    setFilterValues(updatedValues);
    resetActivePage();
  }, [setFilterValues, resetActivePage]);

  const moveSecureNotesModalDescriptionElement = useMemo(
    () => (
      <>
        <Typography>
          {t('moveSecureNotesModal.description', { email: employeeAccount?.email })}
        </Typography>
        <Typography>{t('moveSecureNotesModal.additionalDescription')}</Typography>
      </>
    ),
    [employeeAccount?.email, t],
  );

  const { moveTooltipTitle, shareTooltipTitle, removeTooltipTitle } = useMemo(() => {
    const moveTitle = generateTooltipTitle({
      selectedDataLength: selectedSecureNotesAsObjects.length,
      t,
      isDisabled: isMoveDisabled,
      key: 'secureNotesTab.move',
    });
    const shareTitle = generateTooltipTitle({
      selectedDataLength: selectedSecureNotesAsObjects.length,
      t,
      isDisabled: isShareDisabled,
      key: 'secureNotesTab.share',
    });
    const removeTitle = generateTooltipTitle({
      selectedDataLength: selectedSecureNotesAsObjects.length,
      t,
      isDisabled: isRemoveDisabled,
      key: 'secureNotesTab.remove',
    });

    return {
      moveTooltipTitle: moveTitle,
      shareTooltipTitle: shareTitle,
      removeTooltipTitle: removeTitle,
    };
  }, [selectedSecureNotesAsObjects.length, t, isMoveDisabled, isShareDisabled, isRemoveDisabled]);

  const isCreateSecureNoteShown = useMemo(() => (
    employeeAccountStatus !== EmployeeAccountStatus.Archived
  ), [employeeAccountStatus]);

  const isMoveSecureNoteShown = useMemo(() => (
    employeeAccountStatus !== EmployeeAccountStatus.Archived
    && employeeAccountStatus !== EmployeeAccountStatus.Staged
    && employeeAccountStatus !== EmployeeAccountStatus.Invited
  ), [employeeAccountStatus]);

  return (
    <PanelContent p={0}>
      <Grid container justifyContent="space-between" alignItems="stretch" p={1}>
        <Grid item xs={4} container flexWrap="nowrap" spacing={1}>
          <Grid item>
            <Tooltip title={t('common.filter')}>
              <FilterButton
                isFilterActive={isFilterActive}
                numberOfActiveFilters={numberOfActiveFilters}
                selected={isFilterOpen}
                onChange={toggleIsFilterOpen}
              />
            </Tooltip>
          </Grid>
          <Grid item my={0.5}>
            <Divider orientation="vertical" />
          </Grid>
          {isMoveSecureNoteShown && (
            <Grid item alignSelf="center">
              <Tooltip title={moveTooltipTitle}>
                <ActionButton
                  width={40}
                  height={40}
                  onClick={handleMoveSecureNotesModalOpen}
                  disabled={isMoveDisabled}
                >
                  <MoveIcon />
                </ActionButton>
              </Tooltip>
            </Grid>
          )}
          <Grid item alignSelf="center">
            <Tooltip title={shareTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleShareSecureNotesModalOpen}
                disabled={isShareDisabled}
              >
                <ShareIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
          <Grid item alignSelf="center">
            <Tooltip title={removeTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleDeleteSecureNoteModalOpen}
                disabled={isRemoveDisabled}
              >
                <ErrorIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
          <Grid item my={0.5}>
            <Divider orientation="vertical" />
          </Grid>
        </Grid>
        <Grid item xs={8} container justifyContent="flex-end" flexWrap="nowrap">
          <Grid item>
            <SearchableTextField
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={t('common.search')}
            />
          </Grid>
          {isCreateSecureNoteShown && (
            <Grid item ml={3}>
              <Button
                icon={<PlusIcon />}
                onClick={handleCreateSecureNoteModalOpen}
              >
                {t('secureNotesTab.createSecureNoteButton')}
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Divider />
      <EmployeeSecureNotesTable
        selectedSecureNotes={selectedSecureNotes}
        {...restTableProps}
      />
      <EmployeeSecureNotesTabFilter
        isOpen={isFilterOpen}
        anchorEl={filterAnchorEl}
        onSubmit={handleFilterSubmit}
        onClose={handleFilterClose}
        initialValues={filterValues}
      />
      {isCreateSecureNoteModalOpen && (
        <CreateSecureNoteModal
          isOpen={isCreateSecureNoteModalOpen}
          onClose={handleCreateSecureNoteModalClose}
          onSubmit={handleCreateSecureNote}
          isLoading={isCreateSecureNoteLoading}
        />
      )}
      {isDeleteSecureNoteModalOpen && (
        <PromptModal
          open={isDeleteSecureNoteModalOpen}
          onClose={handleDeleteSecureNoteModalClose}
          onSubmit={handleDeleteSecureNote}
          title={t('deleteSecureNoteFromEmployeeModal.title', { count: selectedSecureNotes.size })}
          description={t(
            'deleteSecureNoteFromEmployeeModal.description',
            { email: employeeAccount?.email },
          )}
          additionalDescription={t('deleteSecureNoteFromEmployeeModal.additionalDescription')}
          list={selectedSecureNotesAsObjects}
          renderField="name"
          renderKey="vaultId"
          isLoading={isDeleteSecureNoteLoading}
        />
      )}
      {isMoveSecureNotesModalOpen && (
        <PromptModal
          open={isMoveSecureNotesModalOpen}
          onClose={handleMoveSecureNotesModalClose}
          onSubmit={handleMoveSecureNotes}
          title={t('moveSecureNotesModal.title', { count: selectedSecureNotes.size })}
          description={moveSecureNotesModalDescriptionElement}
          list={selectedSecureNotesAsObjects}
          renderField="name"
          renderKey="vaultId"
          isLoading={isMoveSecureNotesLoading}
          approvalButtonText="common.move"
          approvalButtonColor="primary"
        />
      )}
      {isShareSecureNotesModalOpen && (
        <ShareVaultsToGroupModal
          isOpen={isShareSecureNotesModalOpen}
          onClose={handleShareSecureNotesModalClose}
          vaults={selectedSecureNotesAsObjects}
          type={VaultTypeEnum.SecureNote}
          onSubmit={resetSelectedRows}
        />
      )}
    </PanelContent>
  );
};

export default memo(EmployeeSecureNotesTab);
